import { useEffect } from "react";

const DEFAULT_OG_IMAGE = "https://customer-assets.emergentagent.com/job_grip-pitch/artifacts/wmpezwr4_5.png";
const SITE_NAME = "Pritch — Premium Pitch Wear";

const setOrCreateMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content" && v) el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  if (attrs.content) el.setAttribute("content", attrs.content);
};

const setOrCreateLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, payload) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

const removeJsonLd = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

/**
 * Lightweight SEO hook. Sets <title>, meta description, OG/Twitter, canonical
 * and (optionally) a JSON-LD payload on mount; restores defaults on unmount.
 */
const useSeo = ({ title, description, path = "/", image, jsonLd, jsonLdId = "page-jsonld" } = {}) => {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const desc =
      description ||
      "Pritch — premium football grip socks engineered for boot-lock fit, close touch and clean matchday style.";
    const url = `https://pritch.fc${path.startsWith("/") ? path : `/${path}`}`;
    const ogImage = image || DEFAULT_OG_IMAGE;

    const prevTitle = document.title;
    document.title = fullTitle;
    setOrCreateMeta('meta[name="description"]', { name: "description", content: desc });
    setOrCreateMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    setOrCreateMeta('meta[property="og:description"]', { property: "og:description", content: desc });
    setOrCreateMeta('meta[property="og:url"]', { property: "og:url", content: url });
    setOrCreateMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
    setOrCreateMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    setOrCreateMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
    setOrCreateMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    setOrCreateLink("canonical", url);

    if (jsonLd) {
      setJsonLd(jsonLdId, jsonLd);
    }

    return () => {
      document.title = prevTitle;
      if (jsonLd) removeJsonLd(jsonLdId);
    };
  }, [title, description, path, image, jsonLd, jsonLdId]);
};

export default useSeo;
